import { Filter, Loader2, Send, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { formatCredits } from '@/lib/creditCosts';
import ContactSelector from '@/components/ContactSelector';
import { CountrySelect } from '@/components/CountrySelect';
import {
  LEAD_STATUS_LABELS,
  LEAD_STATUS_NONE,
  NOT_CONTACTED_LABEL,
  LEAD_CAMPAIGN_LABELS,
  type LeadStatus,
  type LeadCampaign,
} from '@/lib/contactFields';
import { ALL_SOURCES, type AudienceMode, type CampaignLaunchState } from '@/hooks/useCampaignLaunchState';
import { StepFooter, StepHeader } from './StepLayout';

interface AudienceStepProps {
  state: CampaignLaunchState;
  onBack: () => void;
  onNext: () => void;
}

/** Step 4: who receives it, with the recipient count and credit quote kept in view. */
export default function AudienceStep({ state, onBack, onNext }: AudienceStepProps) {
  const {
    workspaceId, mode, setMode, selectedIds, setSelectedIds, filters, setFilters,
    totalContacts, recipientCount, loadingPreview, skippedNoPhone, estimatedCredits, audienceValid,
  } = state;

  return (
    <Card className="p-6">
      <StepHeader step="audience" />

      <Tabs value={mode} onValueChange={(v) => setMode(v as AudienceMode)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="selected" className="gap-2">
            <Users className="h-4 w-4" />
            Select Contacts
          </TabsTrigger>
          <TabsTrigger value="filtered" className="gap-2">
            <Filter className="h-4 w-4" />
            Filter
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2">
            <Send className="h-4 w-4" />
            All Contacts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="selected" className="mt-4">
          <ContactSelector
            workspaceId={workspaceId}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        </TabsContent>

        <TabsContent value="filtered" className="mt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Search Name/Phone/Email</Label>
              <Input
                placeholder="Search..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Source</Label>
              <Select
                value={filters.source || ALL_SOURCES}
                onValueChange={(v) =>
                  setFilters({ ...filters, source: v === ALL_SOURCES ? '' : v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  {/* Radix Select throws on an empty-string item value, so "no filter" needs
                      a sentinel. This previously used value="" and crashed the Filter tab
                      outright — and it also meant the filter could never be cleared. */}
                  <SelectItem value={ALL_SOURCES}>All sources</SelectItem>
                  <SelectItem value="import">Import</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="crm">CRM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Property Interest</Label>
              <Input
                placeholder="e.g., Villa, Apartment"
                value={filters.propertyInterest}
                onChange={(e) =>
                  setFilters({ ...filters, propertyInterest: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Location Preference</Label>
              <Input
                placeholder="e.g., Cairo, Alexandria"
                value={filters.locationPreference}
                onChange={(e) =>
                  setFilters({ ...filters, locationPreference: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Lead Campaign</Label>
              {/* The sentinel again, for the same reason as Source above. */}
              <Select
                value={filters.leadCampaign || ALL_SOURCES}
                onValueChange={(v) =>
                  setFilters({ ...filters, leadCampaign: v === ALL_SOURCES ? '' : v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All lead campaigns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_SOURCES}>All lead campaigns</SelectItem>
                  {(Object.keys(LEAD_CAMPAIGN_LABELS) as LeadCampaign[]).map((campaign) => (
                    <SelectItem key={campaign} value={campaign}>
                      {LEAD_CAMPAIGN_LABELS[campaign]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lead Status</Label>
              <Select
                value={filters.leadStatus || ALL_SOURCES}
                onValueChange={(v) =>
                  setFilters({ ...filters, leadStatus: v === ALL_SOURCES ? '' : v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_SOURCES}>All statuses</SelectItem>
                  <SelectItem value={LEAD_STATUS_NONE}>{NOT_CONTACTED_LABEL}</SelectItem>
                  {(Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((status) => (
                    <SelectItem key={status} value={status}>
                      {LEAD_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Current Country</Label>
              <CountrySelect
                value={filters.currentCountry}
                onChange={(code) => setFilters({ ...filters, currentCountry: code })}
                emptyLabel="All countries"
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Users className="h-16 w-16 text-muted-foreground" />
            <p className="text-lg font-medium">
              Send to all {totalContacts} contacts
            </p>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              This will send the campaign message to all contacts in your workspace
              that have a valid phone number.
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Whichever tab is open, the operator sees what the choice adds up to before moving on. The
          count is the backend's: it excludes contacts without a phone number, which is what is
          actually sent to and charged for. */}
      <div className="mt-5 grid gap-3 rounded-lg bg-muted/50 p-4 sm:grid-cols-2" aria-live="polite">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" /> Recipients
          </span>
          {loadingPreview
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <span className="text-lg font-bold tabular-nums">{recipientCount.toLocaleString()}</span>}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">Estimated credits</span>
          <span className="font-semibold tabular-nums">{formatCredits(estimatedCredits)}</span>
        </div>
        {skippedNoPhone > 0 && (
          <p className="text-xs text-amber-600 sm:col-span-2">
            {skippedNoPhone} of {selectedIds.length} selected contact
            {selectedIds.length === 1 ? '' : 's'} ha
            {skippedNoPhone === 1 ? 's' : 've'} no phone number and will be skipped.
          </p>
        )}
      </div>

      <StepFooter
        onBack={onBack}
        onNext={onNext}
        nextDisabled={!audienceValid || loadingPreview}
        hint={!audienceValid && !loadingPreview ? 'Select at least one contact with a phone number.' : undefined}
      />
    </Card>
  );
}
